import React from "react";
import { Link } from 'react-router-dom';
import { X, Menu, Moon, Sun, Search } from '../../icons';
import TopBar from './TopBar';
import MegaMenu from './MegaMenu';
import MobileNav from './MobileNav';
import mcjLogo from '../../assets/mcjlogo.png';
import haryanaEmblem from '../../assets/haryana-emblem.svg?url';
import digitalIndiaLogo from '../../assets/digital-india.png';
import swachhBharatLogo from '../../assets/swachh-bharat.png';
import nationalEmblem from '../../assets/national-emblem.png';

import { useThemeContext } from '../../context/ThemeContext';
import { useAppConfig } from '../../hooks/useAppConfig';

interface HeaderProps {
    mobileMenuOpen: boolean;
    setMobileMenuOpen: (isOpen: boolean) => void;
    isHeaderVisible: boolean;
    onSearchClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ mobileMenuOpen, setMobileMenuOpen, isHeaderVisible, onSearchClick }) => {
    const { isDarkMode, toggleTheme } = useThemeContext();
    const { config } = useAppConfig();
    const [scrolled, setScrolled] = React.useState(false);

    React.useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const logoUrl = mcjLogo;

    return (
        <header className={`fixed top-0 left-0 right-0 z-50 transition-transform duration-300 ${isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
            }`}>
            <div className={`${scrolled ? 'hidden' : 'block'} transition-all duration-300`}>
                <TopBar />
            </div>

            <div className={`transition-all duration-300 ${scrolled
                ? isDarkMode
                    ? 'bg-slate-900/95 backdrop-blur-md shadow-lg border-b border-white/10'
                    : 'bg-white/95 backdrop-blur-md shadow-md border-b border-gray-100'
                : isDarkMode
                    ? 'bg-slate-900'
                    : 'bg-white'
                }`}>
                <div className={`border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
                    <div className="max-w-7xl mx-auto px-4">
                        <div className={`flex items-center justify-between transition-all duration-300 ${scrolled ? 'py-2' : 'py-3'
                            }`}>
                            <div className="flex items-center gap-3 flex-shrink-0">
                                <Link to="/" className="flex items-center gap-2 group flex-shrink-0">
                                    <img
                                        src={logoUrl}
                                        alt="Municipal Council Jhajjar Logo"
                                        className={`transition-all duration-300 drop-shadow-sm flex-shrink-0 ${scrolled ? 'h-9' : 'h-11'}`}
                                    />
                                    <div className="flex flex-col leading-tight">
                                        <h1 className={`font-bold uppercase ${scrolled ? 'text-sm' : 'text-base'} ${isDarkMode ? 'text-white' : 'text-gray-800'} hidden sm:block`}>
                                            Municipal Council Jhajjar
                                        </h1>
                                        <h1 className={`font-bold text-sm uppercase ${isDarkMode ? 'text-white' : 'text-gray-800'} sm:hidden`}>
                                            MC Jhajjar
                                        </h1>
                                    </div>
                                </Link>
                            </div>

                            <div className="flex items-center gap-3 flex-shrink-0">
                                <a
                                    href="https://haryana.gov.in"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hidden md:flex items-center gap-2 group flex-shrink-0"
                                    title="Government of Haryana"
                                >
                                    <img
                                        src={haryanaEmblem}
                                        alt="Haryana State Emblem"
                                        className={`transition-all duration-300 flex-shrink-0 ${scrolled ? 'h-9' : 'h-11'}`}
                                    />
                                    <div className="flex flex-col leading-tight">
                                        <span className={`font-semibold text-red-600 ${scrolled ? 'text-xs' : 'text-sm'}`}>
                                            Government of Haryana
                                        </span>
                                    </div>
                                </a>

                                <div className={`hidden md:block w-px ${scrolled ? 'h-7' : 'h-9'} ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>

                                <a
                                    href="https://www.digitalindia.gov.in"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Digital India"
                                    className="hidden lg:block flex-shrink-0"
                                >
                                    <img
                                        src={digitalIndiaLogo}
                                        alt="Digital India"
                                        className={`transition-all duration-300 flex-shrink-0 ${scrolled ? 'h-8' : 'h-10'} object-contain`}
                                    />
                                </a>
                                <a
                                    href="https://swachhbharatmission.gov.in"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Swachh Bharat Mission"
                                    className="hidden lg:block flex-shrink-0"
                                >
                                    <img
                                        src={swachhBharatLogo}
                                        alt="Swachh Bharat Mission"
                                        className={`transition-all duration-300 flex-shrink-0 ${scrolled ? 'h-8' : 'h-10'} object-contain`}
                                    />
                                </a>
                                <a
                                    href="https://india.gov.in"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Government of India"
                                    className="hidden lg:block flex-shrink-0"
                                >
                                    <img
                                        src={nationalEmblem}
                                        alt="National Emblem of India"
                                        className={`transition-all duration-300 flex-shrink-0 ${scrolled ? 'h-8' : 'h-10'} object-contain`}
                                    />
                                </a>

                                <div className={`hidden lg:block w-px ${scrolled ? 'h-7' : 'h-9'} ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>

                                <button
                                    onClick={onSearchClick}
                                    className={`hidden md:flex p-2 rounded-lg transition-all duration-200 flex-shrink-0 ${isDarkMode
                                        ? 'hover:bg-white/10 text-gray-300'
                                        : 'hover:bg-gray-100 text-gray-600'
                                        }`}
                                    title="Search"
                                    aria-label="Open search dialog"
                                >
                                    <Search className="w-5 h-5" aria-hidden="true" />
                                </button>

                                <button
                                    onClick={toggleTheme}
                                    className={`hidden md:flex p-2 rounded-lg transition-all duration-200 flex-shrink-0 ${isDarkMode
                                        ? 'hover:bg-white/10 text-yellow-400'
                                        : 'hover:bg-gray-100 text-blue-600'
                                        }`}
                                    title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
                                    aria-label={isDarkMode ? "Switch to light theme" : "Switch to dark theme"}
                                >
                                    {isDarkMode ? <Sun className="w-5 h-5" aria-hidden="true" /> : <Moon className="w-5 h-5" aria-hidden="true" />}
                                </button>

                                <button
                                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                    className={`xl:hidden p-2 rounded-lg transition-all duration-200 flex-shrink-0 ${isDarkMode
                                        ? 'hover:bg-white/10 text-white'
                                        : 'hover:bg-gray-100 text-gray-900'
                                        }`}
                                    aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
                                    aria-expanded={mobileMenuOpen}
                                >
                                    {mobileMenuOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="hidden xl:block">
                    <div className={`max-w-7xl mx-auto px-4 ${isDarkMode ? 'bg-slate-800' : 'bg-gray-50'}`}>
                        <MegaMenu />
                    </div>
                </div>
            </div>

            <MobileNav isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
        </header>
    );
};

export default Header;
